/**
 * Apple IAP wrapper — thin shim around `@capgo/native-purchases` (StoreKit 2).
 *
 * Why a wrapper?
 *  - Lazy-imports the plugin so the web bundle never pulls in native code paths.
 *  - Centralises the post-purchase verification round-trip to our backend so
 *    BillingPage stays declarative.
 *  - Always finishes the StoreKit transaction *after* the backend records it,
 *    matching Apple's recommended StoreKit 2 flow.
 *
 * Server-side: see /app/backend/routers/iap.py (verify + webhook).
 * Setup checklist: see /app/store/IAP_SETUP.md.
 */
import { api } from "./api";
import { isNativeApp, nativePlatform } from "./platform";

const PRODUCT_ID = "daione.pro.monthly";

const onlyNative = () => {
  if (!isNativeApp() || nativePlatform() !== "ios") {
    throw new Error("Apple IAP is only available inside the iOS app.");
  }
};

/**
 * Returns the App Store-localised price string for the Pro Monthly product
 * (e.g. "$9.99"). Falls back to `null` if the plugin can't reach the App Store
 * (e.g. user not signed into Apple ID, IAP product not yet provisioned).
 */
export async function fetchApplePrice() {
  if (!isNativeApp() || nativePlatform() !== "ios") return null;
  try {
    const { NativePurchases } = await import("@capgo/native-purchases");
    const result = await NativePurchases.getProducts({ productIdentifiers: [PRODUCT_ID] });
    const product = result?.products?.[0];
    return product?.priceString || product?.price || null;
  } catch (err) {
    // Plugin not installed in the running shell (e.g. Live Reload in browser)
    // or no products configured yet in App Store Connect.
    console.debug("Apple IAP getProducts failed", err?.message || err);
    return null;
  }
}

/**
 * Drive a StoreKit 2 purchase end-to-end:
 *  1) Plugin presents Apple's purchase sheet.
 *  2) We forward the signed JWS transaction to our backend for verification.
 *  3) Backend updates `pro_until` in Mongo; we return the new state.
 *
 * Throws if the user cancels or anything fails — callers should toast the
 * error message and let the user retry.
 */
export async function purchasePro() {
  onlyNative();
  const { NativePurchases } = await import("@capgo/native-purchases");

  // Optional: attach a UUID to the purchase so Apple can echo it back in
  // future webhooks (App Store Server Notifications). This survives renewals.
  const appAccountToken = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : undefined;

  const result = await NativePurchases.purchaseProduct({
    productIdentifier: PRODUCT_ID,
    ...(appAccountToken ? { applicationUsername: appAccountToken } : {}),
  });

  // Different plugin versions expose the JWS slightly differently — be tolerant.
  const signedTx = result?.transaction?.jwsRepresentation
    || result?.jwsRepresentation
    || result?.signedTransaction
    || null;

  const txId = result?.transaction?.id
    || result?.transactionId
    || result?.transaction_id
    || null;

  if (!signedTx && !txId) {
    throw new Error("StoreKit returned no transaction reference.");
  }

  const { data } = await api.post("/iap/apple/verify", {
    signed_transaction: signedTx || undefined,
    transaction_id: !signedTx ? String(txId) : undefined,
  });
  return data; // { ok, pro_until, status, source }
}

/**
 * "Restore Purchases" — re-syncs the user's StoreKit entitlements with our
 * backend. Apple requires this be a discoverable user action in apps that
 * sell non-consumables / subscriptions.
 */
export async function restoreApplePurchases() {
  onlyNative();
  const { NativePurchases } = await import("@capgo/native-purchases");
  const result = await NativePurchases.restorePurchases();
  const txs = result?.transactions || result?.purchases || [];
  let lastResponse = null;
  for (const tx of txs) {
    const signedTx = tx?.jwsRepresentation || tx?.signedTransaction || null;
    const txId = tx?.id || tx?.transactionId || null;
    if (!signedTx && !txId) continue;
    try {
      const { data } = await api.post("/iap/apple/verify", {
        signed_transaction: signedTx || undefined,
        transaction_id: !signedTx ? String(txId) : undefined,
      });
      lastResponse = data;
    } catch (e) {
      // Skip individual failures so one bad receipt doesn't block the rest.
    }
  }
  return lastResponse;
}

/** Calls our backend's diagnostic endpoint — used by BillingPage to decide
 *  whether to even attempt to render the Subscribe-with-Apple CTA. */
export async function getIapServerStatus() {
  try {
    const { data } = await api.get("/iap/apple/status");
    return data;
  } catch {
    return { configured: false };
  }
}

export const APPLE_PRODUCT_ID = PRODUCT_ID;
